import type { ReactNode } from 'react';

/**
 * Renders one embed kind (the `data-embed` value the core pipeline writes onto
 * `div.mp-embed`). Built-ins register in-process; a third-party plugin would
 * register a sandboxed-surface renderer for the same seam later. `theme` is
 * passed through opaquely (the preview supplies its DiagramTheme).
 */
export interface BlockRenderer {
  embedKind: string;
  render(code: string, theme: unknown): ReactNode;
}

export interface BlockRendererRegistry {
  register(r: BlockRenderer): () => void;
  get(embedKind: string): BlockRenderer | undefined;
  has(embedKind: string): boolean;
  subscribe(cb: () => void): () => void;
}

export function createBlockRendererRegistry(): BlockRendererRegistry {
  const byKind = new Map<string, BlockRenderer>();
  const listeners = new Set<() => void>();
  const emit = (): void => listeners.forEach((l) => l());

  return {
    register(r: BlockRenderer): () => void {
      byKind.set(r.embedKind, r);
      emit();
      return () => {
        if (byKind.get(r.embedKind) === r) {
          byKind.delete(r.embedKind);
          emit();
        }
      };
    },
    get(embedKind: string): BlockRenderer | undefined {
      return byKind.get(embedKind);
    },
    has(embedKind: string): boolean {
      return byKind.has(embedKind);
    },
    subscribe(cb: () => void): () => void {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
  };
}

/** The shared registry the preview resolves embed renderers through. */
export const blockRendererRegistry: BlockRendererRegistry = createBlockRendererRegistry();
